import { useTranslation } from "react-i18next";
import { TextInput } from "@/components/common/text-input";
import { cn } from "@/lib/utils";
import { ALLOW_IMAGE_MIN_SIZE, ALLOW_IMAGE_TYPES } from "@/components/custom-digital-human/constants";
import { useState } from "react";
import { validateImage } from "@/utils/image-validator";
import { useToast } from "@/hooks/use-toast";
import { SkeletonPersonalAgentInfo } from "./skeleton-personal-agent-info";
import { TextAreaInput } from "@/components/common/text-area-input";
import { UserAgentInfoData } from "@/services/agent-v2-users-detail";
import { AvatarUpload } from "@/components/settings-account/avatar-upload";
import { useUserManagementHooks } from "@/manager";
import { PersonalIntroModal } from "./personal-intro-modal";

const Label = ({ label, required = true, className }: { label: string; required?: boolean; className?: string; }) => {
  return (
    <label className={cn('flex w-48 mb-2.5', className)}>
      <span className='text-sm font-medium text-[rgba(52,64,84,1)] mr-0.5'>{label}</span>
      <span className='text-[rgba(117,66,247,1)]'>{required ? '*' : ''}</span>
    </label>
  )
}

// 1MB
const ALLOW_IMAGE_MAX_SIZE = 1 * 1024 * 1024;

interface PersonalAgentInfoProps {
  userAgentInfo: UserAgentInfoData;
  nameError?: string;
  personalTitleError?: string;
  loading?: boolean;
  changeValue: (name: keyof UserAgentInfoData, value: string) => void;
  setInfoTemplateToIntroduction?: () => void;
}

/** 个人智能体信息 */
export const PersonalAgentInfo = ({
  userAgentInfo,
  nameError,
  personalTitleError,
  loading,
  changeValue,
  setInfoTemplateToIntroduction,
}: PersonalAgentInfoProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleChange = (name: keyof UserAgentInfoData, value: string) => {
    changeValue(name, value);
  };
  const { upload, uploading } = useUserManagementHooks.useUploadUserAvatar();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [introModalOpen, setIntroModalOpen] = useState(false);

  const handleUpload = async (file: File) => {
    // 校验文件是否有效
    const isValid = await validateImage(file, {
      allowedTypes: ALLOW_IMAGE_TYPES,
      minFileSize: ALLOW_IMAGE_MIN_SIZE,
      maxFileSize: ALLOW_IMAGE_MAX_SIZE,
    });
    if (!isValid) {
      toast.default({
        title: t("agent_dashboard.user_detail.agent_info.invalid_image"),
        description: t(
          "agent_dashboard.user_detail.agent_info.invalid_image_description"
        ),
      });
      return;
    }
    try {
      await upload(file);
      setSelectedFile(file);
    } catch (error) {
      console.log(error)
    }
  }

  // 个人简介，使用模版
  const handleUseTemplate = () => {
    setInfoTemplateToIntroduction?.();
  }

  if (loading) {
    return <SkeletonPersonalAgentInfo />
  }

  return (
    <div className="relative pb-6 border-b border-[rgba(233,234,235,1)]">
      <div className='text-lg font-semibold text-[rgba(16,24,40,1)] mb-6'>
        <span>{userAgentInfo.name}</span>
        <span>{t("agent_dashboard.user_detail.agent_info.title")}</span>
      </div>
      <div className="flex flex-wrap gap-4">
        <div className='min-w-80 relative'>
          <AvatarUpload
            uploadClassName="px-3 py-1.5"
            avatar={selectedFile ? URL.createObjectURL(selectedFile) : userAgentInfo.avatar}
            title={userAgentInfo?.name || ''}
            onUpload={handleUpload}
          />
        </div>
        <div className='w-80'>
          <Label label={t("agent_dashboard.user_detail.agent_info.name")} />
          <TextInput
            type="text"
            value={userAgentInfo.name}
            className='h-10'
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!nameError}
          />
          <p className={cn(
            'text-xs text-[rgba(102,112,133,1)] pt-1.5',
            nameError && 'text-[rgba(217,45,32,1)]'
          )}>
            {t("agent_dashboard.user_detail.agent_info.name_placeholder")}
          </p>
        </div>
        <div className='flex-1 min-w-80'>
          <Label label={t("agent_dashboard.user_detail.agent_info.person_title")} />
          <TextAreaInput
            type="text"
            value={userAgentInfo.personalTitle}
            className='h-28'
            onChange={(e) => handleChange('personalTitle', e.target.value)}
            error={!!personalTitleError}
          />
          <p className={cn(
            "text-xs text-[rgba(102,112,133,1)] pt-1.5",
            personalTitleError && 'text-[rgba(217,45,32,1)]'
          )}>
            {t("agent_dashboard.user_detail.agent_info.person_title_placeholder")}
          </p>
        </div>
        <div className="flex-1 min-w-80">
          <Label required={false} label={t("agent_dashboard.user_detail.agent_info.intro")} className="mb-[10px]"/>
          <div
            className={cn(
              "w-full h-28 border border-[rgba(208,213,221,1)] pt-2 pb-[10px] px-[14px] rounded-[8px] hover:border-[rgba(102,112,133,1)] cursor-pointer overflow-hidden",
              !userAgentInfo.introduction && "text-[rgba(152,161,179,1)]",
            )}
            onClick={() => setIntroModalOpen(true)}
          >
            <div className="h-full overflow-y-auto whitespace-pre-line text-sm font-normal">
              {userAgentInfo.introduction ? userAgentInfo.introduction : t("agent_dashboard.user_detail.agent_info.intro_placeholder")}
            </div>
          </div>
        </div>
      </div>
      <PersonalIntroModal
        open={introModalOpen}
        introduction={userAgentInfo.introduction}
        onOpenChange={setIntroModalOpen}
        onUseTemplate={handleUseTemplate}
        changeValue={(name, value) => handleChange('introduction', value)}
      />
    </div>
  )
}